import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import DatePicker from 'react-datepicker'
import { registerLocale } from 'react-datepicker'
import fr from 'date-fns/locale/fr'
import 'react-datepicker/dist/react-datepicker.css'
import '../styles/datepicker.css'
import { useCart } from '../contexts/CartContext'
import { useCatalogSettings } from '../contexts/CatalogContext'
import ProductImage from '../components/ProductImage'
import { Package, Trash2, Plus, Minus, Euro, Calendar, StickyNote } from 'lucide-react'
import { apiService as api } from '../services/api'
import { formatCurrency } from '../utils/currency'
import toast from 'react-hot-toast'

// Enregistrer la locale française
registerLocale('fr', fr)

// Fonction pour détecter les jours fériés français et Guadeloupe
const getFrenchHolidays = (year) => {
  const holidays = []
  
  // Jours fériés fixes (France métropolitaine et Guadeloupe)
  holidays.push(new Date(year, 0, 1))   // Jour de l'An
  holidays.push(new Date(year, 4, 1))   // Fête du Travail
  holidays.push(new Date(year, 4, 8))   // Victoire 1945
  holidays.push(new Date(year, 4, 27))  // Abolition de l'esclavage (Guadeloupe)
  holidays.push(new Date(year, 6, 14))  // Fête Nationale
  holidays.push(new Date(year, 7, 15))  // Assomption
  holidays.push(new Date(year, 10, 1))  // Toussaint
  holidays.push(new Date(year, 10, 2))  // Jour des Défunts (Guadeloupe)
  holidays.push(new Date(year, 10, 11)) // Armistice 1918
  holidays.push(new Date(year, 11, 25)) // Noël
  
  // Pâques et jours liés (calculés)
  const easter = calculateEaster(year)
  holidays.push(easter) // Dimanche de Pâques
  
  // Carnaval et jours liés (Guadeloupe)
  // Mardi gras = 47 jours avant Pâques
  holidays.push(new Date(easter.getTime() - 47 * 24 * 60 * 60 * 1000)) // Mardi gras
  // Mercredi des Cendres = 46 jours avant Pâques
  holidays.push(new Date(easter.getTime() - 46 * 24 * 60 * 60 * 1000)) // Mercredi des Cendres
  // Mi-Carême = 24 jours avant Pâques
  holidays.push(new Date(easter.getTime() - 24 * 24 * 60 * 60 * 1000)) // Mi-Carême
  // Vendredi Saint = 2 jours avant Pâques
  holidays.push(new Date(easter.getTime() - 2 * 24 * 60 * 60 * 1000)) // Vendredi Saint
  
  // Lundi de Pâques = 1 jour après Pâques
  holidays.push(new Date(easter.getTime() + 24 * 60 * 60 * 1000)) // Lundi de Pâques
  
  // Ascension = 39 jours après Pâques
  holidays.push(new Date(easter.getTime() + 39 * 24 * 60 * 60 * 1000))
  
  // Lundi de Pentecôte = 50 jours après Pâques
  holidays.push(new Date(easter.getTime() + 50 * 24 * 60 * 60 * 1000))
  
  return holidays
}

// Fonction pour calculer Pâques
const calculateEaster = (year) => {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const n = Math.floor((h + l - 7 * m + 114) / 31)
  const p = (h + l - 7 * m + 114) % 31
  
  return new Date(year, n - 1, p + 1)
}

// Fonction pour obtenir le nom d'un jour férié
const getHolidayName = (date) => {
  const year = date.getFullYear()
  const holidays = getFrenchHolidays(year)
  
  for (const holiday of holidays) {
    if (holiday.getDate() === date.getDate() && 
        holiday.getMonth() === date.getMonth() && 
        holiday.getFullYear() === date.getFullYear()) {
      
      const month = date.getMonth()
      const day = date.getDate()
      
      // Jours fériés fixes
      if (month === 0 && day === 1) return "Jour de l'An"
      if (month === 4 && day === 1) return "Fête du Travail"
      if (month === 4 && day === 8) return "Victoire 1945"
      if (month === 4 && day === 27) return "Abolition de l'esclavage"
      if (month === 6 && day === 14) return "Fête Nationale"
      if (month === 7 && day === 15) return "Assomption"
      if (month === 10 && day === 1) return "Toussaint"
      if (month === 10 && day === 2) return "Jour des Défunts"
      if (month === 10 && day === 11) return "Armistice 1918"
      if (month === 11 && day === 25) return "Noël"
      
      // Jours fériés mobiles (calculés à partir de Pâques)
      const easter = calculateEaster(year)
      const mardiGras = new Date(easter.getTime() - 47 * 24 * 60 * 60 * 1000)
      const mercrediCendres = new Date(easter.getTime() - 46 * 24 * 60 * 60 * 1000)
      const miCareme = new Date(easter.getTime() - 24 * 24 * 60 * 60 * 1000)
      const vendrediSaint = new Date(easter.getTime() - 2 * 24 * 60 * 60 * 1000)
      const easterMonday = new Date(easter.getTime() + 24 * 60 * 60 * 1000)
      const ascension = new Date(easter.getTime() + 39 * 24 * 60 * 60 * 1000)
      const pentecostMonday = new Date(easter.getTime() + 50 * 24 * 60 * 60 * 1000)
      
      if (date.getTime() === mardiGras.getTime()) return "Mardi gras"
      if (date.getTime() === mercrediCendres.getTime()) return "Mercredi des Cendres"
      if (date.getTime() === miCareme.getTime()) return "Mi-Carême"
      if (date.getTime() === vendrediSaint.getTime()) return "Vendredi Saint"
      if (date.getTime() === easter.getTime()) return "Dimanche de Pâques"
      if (date.getTime() === easterMonday.getTime()) return "Lundi de Pâques"
      if (date.getTime() === ascension.getTime()) return "Ascension"
      if (date.getTime() === pentecostMonday.getTime()) return "Lundi de Pentecôte"
    }
  }
  
  return null
}

// Fonction pour vérifier si une date est un jour férié
const isHoliday = (date) => {
  return getHolidayName(date) !== null
}

// Fonction pour calculer la date de récupération du matériel
const calculatePickupDate = (startDate) => {
  if (!startDate) return null
  
  // Créer une copie de la date à minuit pour éviter les problèmes de timezone
  const start = new Date(startDate)
  start.setHours(0, 0, 0, 0)
  
  const pickupDate = new Date(start)
  pickupDate.setDate(pickupDate.getDate() - 1) // 1 jour avant
  
  // Gérer les week-ends : si le retrait tombe un samedi ou dimanche, on recule au vendredi
  const dayOfWeek = pickupDate.getDay()
  if (dayOfWeek === 0) { // Dimanche → Vendredi (recule de 2 jours)
    pickupDate.setDate(pickupDate.getDate() - 2)
  } else if (dayOfWeek === 6) { // Samedi → Vendredi (recule de 1 jour)
    pickupDate.setDate(pickupDate.getDate() - 1)
  }
  
  return pickupDate
}

// Fonction pour calculer la date de retour du matériel
const calculateReturnDate = (endDate) => {
  if (!endDate) return null
  
  // Créer une copie de la date à minuit pour éviter les problèmes de timezone
  const end = new Date(endDate)
  end.setHours(0, 0, 0, 0)
  
  const returnDate = new Date(end)
  returnDate.setDate(returnDate.getDate() + 1) // 1 jour après
  
  // Gérer les week-ends : si le retour tombe un samedi ou dimanche, on avance au lundi
  const dayOfWeek = returnDate.getDay()
  if (dayOfWeek === 6) { // Samedi → Lundi (avance de 2 jours)
    returnDate.setDate(returnDate.getDate() + 2)
  } else if (dayOfWeek === 0) { // Dimanche → Lundi (avance de 1 jour)
    returnDate.setDate(returnDate.getDate() + 1)
  }
  
  return returnDate
}

// Fonction pour vérifier si une date est aujourd'hui
const isToday = (date) => {
  if (!date) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const checkDate = new Date(date)
  checkDate.setHours(0, 0, 0, 0)
  return checkDate.getTime() === today.getTime()
}

// Fonction pour calculer les jours de location (week-end = 1 jour)
const calculateRentalDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0
  
  const start = new Date(startDate)
  const end = new Date(endDate)
  let days = 0
  const current = new Date(start)
  let weekendDays = 0
  
  // Parcourir chaque jour de la période
  while (current <= end) {
    const dayOfWeek = current.getDay()
    // Compter les jours de week-end (samedi = 6, dimanche = 0)
    if (dayOfWeek === 6 || dayOfWeek === 0) {
      weekendDays++
    } else {
      days++
    }
    current.setDate(current.getDate() + 1)
  }
  
  // Si on a des jours de week-end, on compte 1 jour pour tout le week-end
  if (weekendDays > 0) {
    days += 1
  }
  
  return Math.max(1, days) // Minimum 1 jour
}

const Panier = () => {
  const { items, removeFromCart, clearCart, setItemQuantity } = useCart()
  const { settings: catalogSettings } = useCatalogSettings()
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  // Réglages publics (backend): contrôle global d'affichage
  const { data: adminSettingsData } = useQuery(
    'rentman-settings-public',
    () => api.get('/rentman/settings/public').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )
  const backendShowPrice = adminSettingsData?.show_regular_price
  const showPrice = backendShowPrice === true
  const { data: companyData } = useQuery('company-public', () => api.get('/rentman/company/public').then(r=>r.data), { staleTime: 5*60*1000 })
  const currency = companyData?.company?.currency || 'EUR'
  const currencyPos = companyData?.company?.currency_position || 'right'

  // Helpers stockage
  const toIso = (d) => d ? d.toISOString().slice(0,10) : ''
  const parseLocalDate = (iso) => {
    if (!iso) return null
    // Construire une date locale à minuit pour éviter les décalages
    const [y,m,day] = iso.split('-').map(Number)
    if (!y || !m || !day) return null
    return new Date(y, m-1, day)
  }

  // Hydrater depuis localStorage au montage
  useEffect(() => {
    try {
      const s = localStorage.getItem('rental_start')
      const e = localStorage.getItem('rental_end')
      if (s) setStartDate(parseLocalDate(s))
      if (e) setEndDate(parseLocalDate(e))
    } catch {}
  }, [])

  // Vider les dates quand le panier est vidé
  useEffect(() => {
    if (items.length === 0) {
      setStartDate(null)
      setEndDate(null)
      localStorage.removeItem('rental_start')
      localStorage.removeItem('rental_end')
    }
  }, [items.length])

  // Persister sur changement
  useEffect(() => {
    try {
      if (startDate) localStorage.setItem('rental_start', toIso(startDate))
      if (!startDate) localStorage.removeItem('rental_start')
    } catch {}
  }, [startDate])

  useEffect(() => {
    try {
      if (endDate) localStorage.setItem('rental_end', toIso(endDate))
      if (!endDate) localStorage.removeItem('rental_end')
    } catch {}
  }, [endDate])
  const navigate = useNavigate()

  const total = items.reduce((sum, p) => sum + (p.price_per_day || 0) * (p.quantity || 1), 0)

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">
            <span className="text-gradient">Listes d'equipement</span>
          </h1>
          <p className="text-xl text-gray-600">Vos articles réservés</p>
        </div>

        {items.length === 0 ? (
          <div className="card text-center py-12">
            <Package className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Votre panier est vide</h3>
            <Link to="/catalogue-materiels" className="btn-primary inline-flex items-center mt-2">Aller au catalogue</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {items.map(item => (
                <div key={item.id} className="card">
                  {/* Layout mobile : photo à gauche, titre à droite */}
                  <div className="flex items-start gap-4 md:items-center">
                    <div className="h-20 w-20 bg-white rounded-lg overflow-hidden flex-shrink-0">
                      <ProductImage imageUrl={item.image_url} alt={item.name} className="w-full h-full object-cover" fallbackIcon={Package} fallbackIconSize="h-8 w-8" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{item.name}</h3>
                      <div className="hidden md:block text-sm text-gray-600">{item.category_name}</div>
                      {showPrice && (
                        <div className="mt-1 flex items-center space-x-2">
                          <Euro className="h-4 w-4 text-orange-500" />
                          <span className="font-semibold">{formatCurrency(item.price_per_day || 0, currency, currencyPos)}</span>
                          <span className="text-sm text-gray-600">/jour</span>
                        </div>
                      )}
                      
                      {/* Contrôles quantité et suppression - en dessous du titre sur mobile */}
                      <div className="mt-3 flex items-center justify-between md:hidden">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Qté</span>
                          <button className="btn-outline px-2" onClick={() => setItemQuantity(item.id, (item.quantity || 1) - 1)}><Minus className="h-4 w-4" /></button>
                          <input className="w-16 border border-gray-300 rounded px-2 py-1 text-center bg-white text-gray-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" type="number" min="1" value={item.quantity || 1} onChange={(e) => setItemQuantity(item.id, Math.max(1, Number(e.target.value)))} />
                          <button className="btn-outline px-2" onClick={() => setItemQuantity(item.id, (item.quantity || 1) + 1)}><Plus className="h-4 w-4" /></button>
                        </div>
                        <button className="btn-outline px-2" aria-label="Retirer" title="Retirer" onClick={() => removeFromCart(item.id)}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Contrôles quantité et suppression - à droite sur desktop */}
                    <div className="hidden md:flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Qté</span>
                      <button className="btn-outline px-2" onClick={() => setItemQuantity(item.id, (item.quantity || 1) - 1)}><Minus className="h-4 w-4" /></button>
                      <input className="w-16 border border-gray-300 rounded px-2 py-1 text-center bg-white text-gray-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" type="number" min="1" value={item.quantity || 1} onChange={(e) => setItemQuantity(item.id, Math.max(1, Number(e.target.value)))} />
                      <button className="btn-outline px-2" onClick={() => setItemQuantity(item.id, (item.quantity || 1) + 1)}><Plus className="h-4 w-4" /></button>
                    </div>
                    <button className="hidden md:block btn-outline px-2" aria-label="Retirer" title="Retirer" onClick={() => removeFromCart(item.id)}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <button className="btn-outline" onClick={clearCart}>Vider le panier</button>
            </div>
            <div>
              <div className="card mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 text-orange-500 mr-2" />
                  Date de tournage prévue
                </h3>
                <div className="space-y-4">
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Version Mobile - Inputs natifs */}
                    <div className="md:hidden date-input-container">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date de départ</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="date"
                          value={startDate ? startDate.toISOString().split('T')[0] : ''}
                          onChange={(e) => {
                            const date = e.target.value ? new Date(e.target.value) : null
                            
                            // Validation : ne pas permettre les dates dans le passé
                            if (date && date < new Date(new Date().setHours(0, 0, 0, 0))) {
                              toast.error('La date de départ ne peut pas être dans le passé')
                              return
                            }
                            
                            setStartDate(date)
                            
                            // Si la date de retour devient invalide, la vider
                            if (endDate && date && endDate < date) {
                              setEndDate(null)
                              toast.info('Date de retour effacée car antérieure à la nouvelle date de départ')
                            }
                          }}
                          min={new Date().toISOString().split('T')[0]}
                          max={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                          className="input-field pl-10 w-full text-sm"
                          placeholder="Sélectionner une date"
                          style={{ 
                            fontSize: '16px',
                            minHeight: '44px',
                            width: '100%',
                            minWidth: '200px'
                          }}
                        />
                      </div>
                    </div>
                    
                    <div className="md:hidden date-input-container">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date de retour</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="date"
                          value={endDate ? endDate.toISOString().split('T')[0] : ''}
                          onChange={(e) => {
                            const date = e.target.value ? new Date(e.target.value) : null
                            
                            // Validation : ne pas permettre les dates dans le passé
                            if (date && date < new Date(new Date().setHours(0, 0, 0, 0))) {
                              toast.error('La date de retour ne peut pas être dans le passé')
                              return
                            }
                            
                            // Validation : ne pas permettre une date de retour antérieure à la date de départ
                            if (date && startDate && date < startDate) {
                              toast.error('La date de retour ne peut pas être antérieure à la date de départ')
                              return
                            }
                            
                            setEndDate(date)
                          }}
                          min={startDate ? startDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                          max={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                          className="input-field pl-10 w-full text-sm"
                          placeholder="Sélectionner une date"
                          style={{ 
                            fontSize: '16px',
                            minHeight: '44px',
                            width: '100%',
                            minWidth: '200px'
                          }}
                        />
                      </div>
                    </div>

                    {/* Version Desktop - DatePicker */}
                    <div className="hidden md:block relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date de départ</label>
                      <div className="relative">
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => {
                            // Validation : ne pas permettre les dates dans le passé
                            if (date && date < new Date(new Date().setHours(0, 0, 0, 0))) {
                              toast.error('La date de départ ne peut pas être dans le passé')
                              return
                            }
                            
                            setStartDate(date)
                            
                            // Si la date de retour devient invalide, la vider
                            if (endDate && date && endDate < date) {
                              setEndDate(null)
                              toast.info('Date de retour effacée car antérieure à la nouvelle date de départ')
                            }
                          }}
                          locale="fr"
                          calendarStartDay={1}
                          minDate={new Date()}
                          maxDate={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)}
                          dateFormat="dd/MM/yyyy"
                          className="input-field w-full"
                          placeholderText="Sélectionner une date"
                          showPopperArrow={false}
                          popperPlacement="bottom-start"
                          calendarClassName="shadow-lg border border-gray-200 rounded-lg"
                          dayClassName={(date) => {
                            const today = new Date()
                            const isToday = date.toDateString() === today.toDateString()
                            const isPast = date < today
                            const isWeekend = date.getDay() === 0 || date.getDay() === 6
                            
                            if (isPast) return "text-gray-300 cursor-not-allowed"
                            if (isToday) return "bg-orange-100 text-orange-600 font-semibold"
                            if (isWeekend) return "text-orange-500"
                            return "hover:bg-orange-50"
                          }}
                          monthsShown={1}
                          withPortal={false}
                          inline={false}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                    
                    <div className="hidden md:block relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date de retour</label>
                      <div className="relative">
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => {
                            // Validation : ne pas permettre les dates dans le passé
                            if (date && date < new Date(new Date().setHours(0, 0, 0, 0))) {
                              toast.error('La date de retour ne peut pas être dans le passé')
                              return
                            }
                            
                            // Validation : ne pas permettre une date de retour antérieure à la date de départ
                            if (date && startDate && date < startDate) {
                              toast.error('La date de retour ne peut pas être antérieure à la date de départ')
                              return
                            }
                            
                            setEndDate(date)
                          }}
                          locale="fr"
                          calendarStartDay={1}
                          minDate={startDate || new Date()}
                          maxDate={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)}
                          dateFormat="dd/MM/yyyy"
                          className="input-field w-full"
                          placeholderText="Sélectionner une date"
                          showPopperArrow={false}
                          popperPlacement="bottom-start"
                          calendarClassName="shadow-lg border border-gray-200 rounded-lg"
                          dayClassName={(date) => {
                            const today = new Date()
                            const isToday = date.toDateString() === today.toDateString()
                            const isPast = date < (startDate || today)
                            const isWeekend = date.getDay() === 0 || date.getDay() === 6
                            
                            if (isPast) return "text-gray-300 cursor-not-allowed"
                            if (isToday) return "bg-orange-100 text-orange-600 font-semibold"
                            if (isWeekend) return "text-orange-500"
                            return "hover:bg-orange-50"
                          }}
                          monthsShown={1}
                          withPortal={false}
                          inline={false}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  </div>
                  {startDate && endDate && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          {(() => {
                            // Calculer les jours en excluant les dimanches
                            const days = calculateRentalDays(startDate, endDate)
                            const startFormatted = startDate.toLocaleDateString('fr-FR', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })
                            const endFormatted = endDate.toLocaleDateString('fr-FR', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })
                            
                            // Calculer les dates de récupération et retour
                            const pickupDate = calculatePickupDate(startDate)
                            const returnDate = calculateReturnDate(endDate)
                            const pickupFormatted = pickupDate ? pickupDate.toLocaleDateString('fr-FR', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            }) : ''
                            const returnFormatted = returnDate ? returnDate.toLocaleDateString('fr-FR', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            }) : ''
                            
                            return (
                              <div className="space-y-2">
                                <div className="space-y-1">
                                  <div><strong>Durée:</strong> {days} jour{days > 1 ? 's' : ''}</div>
                                  <div className="text-xs text-gray-600">
                                    {days === 1 ? (
                                      <div>{startFormatted}</div>
                                    ) : (
                                      <>
                                        <div>Du {startFormatted}</div>
                                        <div>Au {endFormatted}</div>
                                      </>
                                    )}
                                  </div>
                                </div>
                                {(pickupFormatted || returnFormatted) && (
                                  <div className="pt-2 border-t border-gray-200">
                                    <div className="text-sm font-medium text-gray-700 mb-1">
                                      <span>Info retrait :</span>
                                    </div>
                                    <div className="text-xs text-gray-600 space-y-1">
                                      {pickupFormatted && (
                                        <div><strong>Départ :</strong> {isToday(startDate) ? (
                                          <>Le matériel sera à récupérer ce jour à partir de <u>15h</u> sous réserve de disponibilité du matériel.</>
                                        ) : (
                                          <>Le matériel sera à récupérer le <u>{pickupFormatted}</u> à partir de <u>15h</u></>
                                        )}</div>
                                      )}
                                      {returnFormatted && (
                                        <div><strong>Retour :</strong> Prévu le <u>{returnFormatted}</u> avant <u>11h</u></div>
                                      )}
                                    </div>
                                    {/* Alerte pour les jours fériés */}
                                    {(() => {
                                      const pickupDate = calculatePickupDate(startDate)
                                      const returnDate = calculateReturnDate(endDate)
                                      const pickupHoliday = pickupDate ? getHolidayName(pickupDate) : null
                                      const returnHoliday = returnDate ? getHolidayName(returnDate) : null
                                      
                                      if (pickupHoliday || returnHoliday) {
                                        return (
                                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                            <div className="text-xs text-yellow-800">
                                              <div className="font-medium mb-1">⚠️ Attention :</div>
                                              {pickupHoliday && (
                                                <div className="mb-1">
                                                  Nous serons potentiellement fermés le <strong>{pickupDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong> car c'est <strong>{pickupHoliday}</strong>. Merci de nous contacter pour le retrait.
                                                </div>
                                              )}
                                              {returnHoliday && (
                                                <div>
                                                  Nous serons potentiellement fermés le <strong>{returnDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong> car c'est <strong>{returnHoliday}</strong>. Merci de nous contacter pour le retour.
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )
                                      }
                                      return null
                                    })()}
                                  </div>
                                )}
                              </div>
                            )
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    className="btn-primary w-full"
                    onClick={() => {
                      if (!startDate || !endDate) {
                        toast.error('Merci de renseigner les dates')
                        return
                      }
                      const isLogged = !!localStorage.getItem('user_token')
                      const qs = `?start=${toIso(startDate)}&end=${toIso(endDate)}`
                      if (!isLogged) {
                        navigate(`/login?redirect=${encodeURIComponent('/checkout'+qs)}`)
                        return
                      }
                      navigate('/checkout'+qs)
                    }}
                  >
                    Demande de devis
                  </button>
                </div>
              </div>
              
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Panier


